// ENG-SOT-050 — the /contact-us screen of the compiled Kit 2.01 design, made to use the tested intake.
//
// The design's contact screen is a dummy: eight uncontrolled inputs, TOPIC toggles wired to local state, a
// SUBMIT button bound to `noop`, an unverified "We answer inside one business day." promise and a
// ride@snowbus.com mailbox presented as the staffed fallback. None of it reaches /api/feedback.
//
// This is the narrow, contact-only seam: a pure transform over the compiled AST (`DESIGN_AST`, generated and
// NOT edited) that
//   · swaps the dummy form card's CHILDREN for the real FeedbackForm, in the form's own slot — the card, the
//     page header, the FAQ aside, the Kit header/footer/nav and every other screen are untouched;
//   · drops the one-business-day promise from the contact lead line;
//   · drops the unverified email block from the aside (the "A Canadian company" line stays).
// The runtime builds elements with `createElement(node.tag, …)`, so a component is a valid `tag`; it renders
// identically on the server and in the hydrating client.
//
// THROW ON MISS, like the build-time patches in design-sources.ts: if the design is regenerated and a node no
// longer matches, this fails loudly (and the contact tests go red) instead of silently serving the dummy form.
// Nodes are matched by what they are (screen label, form controls, the promise text, the mailto link), never by
// the compiler's generated numeric ids, which shift whenever any earlier screen changes. Every match must be
// unique: zero or several candidates is a miss.
import type { ComponentType } from "react";

export interface AstNode {
  k: string;
  id?: string;
  tag?: string | ComponentType;
  s?: string;
  p?: [string, string][];
  c?: AstNode[];
  [key: string]: unknown;
}

const SLA = " We answer inside one business day.";

function flat(nodes: AstNode[], out: AstNode[] = []): AstNode[] {
  for (const n of nodes) {
    out.push(n);
    if (n.c) flat(n.c, out);
  }
  return out;
}

function must(nodes: AstNode[], check: (n: AstNode) => boolean, what: string): AstNode {
  const hits = flat(nodes).filter((n) => n.k === "e" && check(n));
  const [hit] = hits;
  if (!hit || hits.length !== 1) throw new Error(`[design-contact-patch] contact screen changed: ${what} not found (${hits.length} matches) — regenerate the patch`);
  return hit;
}

const prop = (n: AstNode, key: string): string | undefined => n.p?.find(([k]) => k === key)?.[1];
const textOf = (n: AstNode): string => (n.k === "t" ? (n.s ?? "") : (n.c ?? []).map(textOf).join(""));

/** Returns a patched copy of the AST; the input (the generated module's export) is never mutated. */
export function patchContactSurface(ast: readonly AstNode[], Form: ComponentType): AstNode[] {
  const out = structuredClone(ast) as AstNode[];

  const screen = must(out, (n) => n.tag === "main" && prop(n, "data-screen-label") === "Contact us", "contact screen");
  const inScreen = screen.c ?? [];

  const lead = must(inScreen, (n) => (n.c ?? []).some((c) => c.k === "t" && (c.s ?? "").includes(SLA.trim())), "lead line with the business-day promise");
  const leadText = (lead.c ?? []).find((c) => c.k === "t" && (c.s ?? "").includes(SLA.trim()));
  if (!leadText?.s) throw new Error("[design-contact-patch] contact lead text node missing");
  leadText.s = leadText.s.replace(SLA, "");

  // The dummy card is the element that directly holds the SUBMIT button and contains the message textarea.
  const card = must(
    inScreen,
    (n) => n.tag === "div" && (n.c ?? []).some((c) => c.tag === "button" && textOf(c).trim() === "Submit") && flat(n.c ?? []).some((c) => c.tag === "textarea"),
    "dummy form card",
  );
  card.c = [{ k: "e", tag: Form, id: "feedback-form", p: [], c: [] }];

  const label = must(inScreen, (n) => n.tag === "div" && textOf(n).trim() === "Email", "aside Email label");
  const link = must(inScreen, (n) => n.tag === "a" && (prop(n, "href") ?? "").startsWith("mailto:"), "aside mailto link");
  const parent = (function parentOf(nodes: AstNode[]): AstNode | null {
    for (const n of nodes) {
      if (n.c?.includes(label)) return n;
      const hit = n.c ? parentOf(n.c) : null;
      if (hit) return hit;
    }
    return null;
  })(screen.c ?? []);
  if (!parent?.c?.includes(link)) throw new Error("[design-contact-patch] aside email block changed");
  parent.c = parent.c.filter((c) => c !== label && c !== link);

  return out;
}
